import { html, css, LitElement } from 'lit';
import { Image } from '../Image/Image';
import { Text } from '../Text/Text';
import { MessageUnreadCount } from '../MessageUnreadCount/MessageUnreadCount';
import '../ReadMark/BlueReadMark/BlueReadMark';
import '../ReadMark/DoubleMark/DoubleMark';
import '../ReadMark/ClockMark/ClockMark';
import '../ReadMark/SingleMark/SingleMark';
import { generateProfileImageByInitials } from '../../../lib/helpers';
import { getImageSrc } from '../../../utils/utils';

class ChatTile extends LitElement {
  static properties = {
    // Image properties
    imageSrc: { type: String },
    imageAlt: { type: String },
    imageHeight: { type: String },
    imageWidth: { type: String },
    imageRadius: { type: String },
    imageFillColor: { type: String },
    imageTextColor: { type: String },

    // Username properties
    username: { type: String },
    userNameColor: { type: String },
    userNameFontFamily: { type: String },
    userNameFontWeight: { type: String },
    usernameFontSize: { type: String },
    usernameFontStyle: { type: String },

    // Chat preview properties
    chatPreview: { type: String },
    chatPreviewColor: { type: String },
    chatPreviewFontFamily: { type: String },
    chatPreviewFontWeight: { type: String },
    chatPreviewFontSize: { type: String },
    chatPreviewFontStyle: { type: String },

    // Unread count properties
    unreadCount: { type: Number },
    unreadCountColor: { type: String },
    unreadCountFontSize: { type: String },
    unreadCountBgColor: { type: String },
    unreadCountBorderRadius: { type: String },
    unreadCountWidth: { type: String },
    unreadCountHeight: { type: String },

    // Message date properties
    messageDate: { type: String },
    messageDateColor: { type: String },
    messageDateFontFamily: { type: String },
    messageDateFontWeight: { type: String },
    messageDateFontSize: { type: String },
    messageDateFontStyle: { type: String },

    // Layout properties
    padding: { type: String },
    usernamePadding: { type: String },
    chatPreviewPadding: { type: String },
    messageDatePadding: { type: String },
    bgColor: { type: String },
    borderWidth: { type: String },
    borderColor: { type: String },
    borderStyle: { type: String },
    height: { type: String },
    width: { type: String },

    // State properties
    isSelected: { type: Boolean },
    sidebarSelectedColor: { type: String },

    // Additional properties
    suffix: { type: String },
    suffixColor: { type: String },
    suffixBgColor: { type: String },
    gap: { type: String },
    isTyping: { type: Boolean },
    typingUserName: { type: String },
    isRead: { type: Boolean },
    isPending: { type: Boolean },
    isDelivered: { type: String },
    isSelfMessage: { type: Boolean },
    isBuyNow: { type: Boolean },
    isMakeOffer: { type: Boolean },
    isExchangeOffer: { type: Boolean },
    senderProfileImageURL: { type: String },
    offerAmount: { type: String },
    mediaType: { type: Number },
  };

  static styles = css`
    .chat-tile-container {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: 16px;
      cursor: pointer;
      padding: 12px 16px;
      min-height: 80px;
      transition: border-color 0.2s ease, border-width 0.2s ease;
      border-radius: 10px;
    }

    .chat-tile-container:hover {
      border-width: 1px !important;
      border-color: var(--brand-color) !important;
    }

    .content-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      gap: 6px;
    }

    .username-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
      gap: 12px;
    }

    .username {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .message-date {
      flex-shrink: 0;
      white-space: nowrap;
    }

    .unread-count {
      height: 20px;
      width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .chat-preview-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      gap: 12px;
    }

    .offer-amount {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .bottom-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      gap: 12px;
    }

    .chat-preview {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }

    .action-button {
      flex-shrink: 0;
      white-space: nowrap;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      border: none;
      min-width: 80px;
      text-align: center;
    }

    .message-status {
      display: inline-flex;
      align-items: center;
      height: 16px;
      flex-shrink: 0;
      margin-right: 4px;
    }
  `;

  constructor() {
    super();
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('isRead') || changedProperties.has('isDelivered') || changedProperties.has('isPending')) {
      this.requestUpdate();
    }

  }
  render() {

    return html`
      <div
        class="chat-tile-container"
        style="
          position: relative;
          background-color: ${this.bgColor};
          border-width: ${this.isSelected ? '1px' : this.borderWidth};
          border-color: ${this.isSelected ? 'var(--brand-color)' : this.borderColor};
          border-style: ${this.borderStyle};
          height: ${this.height || 'auto'};
          width: ${this.width || '100%'};
          border-radius: 10px;
          gap: ${this.gap || '16px'};
        "
      >
        ${this.suffix
          ? html`
              <div
                class="suffix-container"
                style="
                  padding: 3px 10px;
                  border-radius: 12px;
                  font-size: 14px;
                  background-color: ${this.suffixBgColor};
                  color: ${this.suffixColor};
                  position: absolute;
                  top: 25%;
                  right: 10px;
                "
              >
                ${this.suffix}
              </div>
            `
          : ''}

        <div
          class="image-container"
          style="position: relative; height: ${this.imageHeight}; width: ${this.imageWidth}"
        >
          ${Image({
            src: getImageSrc(this.imageSrc),
            alt: 'chat image',
            height: this.imageHeight,
            width: this.imageWidth,
            borderRadius: this.imageRadius,
            objectFit: 'cover',
            placeholderImage: generateProfileImageByInitials(this.username),
          })}
          <div
            class="user-image-container"
            style="position: absolute; bottom: -10px; right: -10px;"
          >
            ${Image({
              src: this.senderProfileImageURL,
              alt: 'chat image',
              height: '20px',
              width: '20px',
              borderRadius: '50%',
              borderStyle: 'solid',
              borderColor: '#fff',
              border: '2px',
              objectFit: 'cover',
              placeholderImage: generateProfileImageByInitials(this.username),
            })}
          </div>
        </div>

        <div class="content-container">
          <div class="username-container">
            <div class="username" style="padding: ${this.usernamePadding};">
              ${Text({
                content: this.username,
                color: this.userNameColor,
                fontFamily: this.userNameFontFamily,
                fontWeight: this.userNameFontWeight,
                fontSize: this.usernameFontSize,
                fontStyle: this.usernameFontStyle,
                textAlign: 'left',
                overflow: 'hidden',
              })}
            </div>

            <div class="message-date" style="padding: ${this.messageDatePadding};">
              ${Text({
                content: this.messageDate,
                color: this.messageDateColor,
                fontFamily: this.messageDateFontFamily,
                fontWeight: this.messageDateFontWeight,
                fontSize: this.messageDateFontSize,
                fontStyle: this.messageDateFontStyle,
              })}
            </div>
          </div>

          <div class="chat-preview-container">
          ${ !this.offerAmount.includes('undefined') ? html`
             <div class="offer-amount">
              ${Text({
                content: this.offerAmount,
                color: this.userNameColor,
                fontFamily: this.userNameFontFamily,
                fontWeight: '500',
                fontSize: this.usernameFontSize,
                fontStyle: this.usernameFontStyle,
                textAlign: 'left',
                overflow: 'hidden',
              })}
            </div>` : ''}
           

            ${this.unreadCount > 0 ? html`
              <div class="unread-count">
                ${MessageUnreadCount({
                  content: this.unreadCount,
                  color: this.unreadCountColor,
                  fontSize: this.unreadCountFontSize,
                  backgroundColor: this.unreadCountBgColor,
                  borderRadius: this.unreadCountBorderRadius,
                  width: this.unreadCountWidth,
                  height: this.unreadCountHeight,
                })}
              </div>
            ` : ''}
          </div>

          <div class="bottom-row">
            <div class="chat-preview" style="padding: ${this.chatPreviewPadding};">
              ${this.isSelfMessage
                ? html`
                    <span class="message-status">
                      ${this.isRead
                        ? html`<blue-read-mark></blue-read-mark>`
                        : this.isDelivered === 'true'
                        ? html`<double-mark svgFillColor="${this.chatPreviewColor}"></double-mark>`
                        : this.isDelivered === 'pending'
                        ? html`<clock-mark svgFillColor="${this.chatPreviewColor}"></clock-mark>`
                        : html`<single-mark></single-mark>`}
                    </span>
                  `
                : ''}
              ${Text({
                content: this.chatPreview,
                color: this.chatPreviewColor,
                fontFamily: this.chatPreviewFontFamily,
                fontWeight: this.chatPreviewFontWeight,
                fontSize: this.chatPreviewFontSize,
                fontStyle: this.chatPreviewFontStyle,
                textAlign: 'left',
                overflow: 'hidden',
              })}
            </div>
            ${this.isBuyNow || this.isMakeOffer || this.isExchangeOffer ? html`
              <div class="action-button" style="
                background-color: ${this.isBuyNow ? '#000000' : '#0066CC'};
                color: #FFFFFF;
              ">
                ${this.isBuyNow ? 'Buy Now' : this.isExchangeOffer ? 'Exchange' : 'Make Offer'}
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('chat-tile', ChatTile);
